import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Select, MenuItem, FormControl, InputLabel,
  Button, CircularProgress, Alert, Chip, TextField, Autocomplete, Card,
  CardContent, CardHeader, Divider, Stack, alpha, useTheme,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel, IconButton
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PieChartIcon from '@mui/icons-material/PieChart';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import PercentIcon from '@mui/icons-material/Percent';
import Filter1Icon from '@mui/icons-material/Filter1';
import Filter2Icon from '@mui/icons-material/Filter2';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import EditIcon from '@mui/icons-material/Edit';
import { dreamTeamAnalysisApi, seriesApi, groundApi } from '../../services/api';

const ROLE_COLORS: Record<string, string> = { BAT: '#4fc3f7', BOWL: '#ef5350', ALL: '#66bb6a', WK: '#ffa726' };
const PIE_COLORS = ['#26a69a', '#5c6bc0', '#ec407a', '#ffa726', '#7c4dff'];

// ── Shared table helper ──
const pct = (val: number, total: number) => total > 0 ? `${((val / total) * 100).toFixed(1)}%` : '-';

const StyledTable: React.FC<{ headers: string[]; rows: (string | number)[][] }> = ({ headers, rows }) => {
  const theme = useTheme();
  return (
    <TableContainer sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
            {headers.map((h) => <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12 }}>{h}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{ '&:nth-of-type(odd)': { bgcolor: alpha(theme.palette.action.hover, 0.03) } }}>
              {row.map((cell, j) => (
                <TableCell key={j} sx={{ fontSize: 13 }}>
                  {j === 0 ? <Chip label={cell} size="small" sx={{ fontWeight: 700, fontSize: 11, bgcolor: alpha(ROLE_COLORS[cell as string] || theme.palette.primary.main, 0.12), color: ROLE_COLORS[cell as string] || theme.palette.primary.main }} /> : cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ── Stat Renderers ──

const CvcDistributionRenderer: React.FC<{ data: any }> = ({ data }) => {
  if (!data) return null;
  const total = data.total_matches || 1;
  const roles = [...new Set([...Object.keys(data.captain || {}), ...Object.keys(data.vice_captain || {})])];

  const mainRows = roles.map((r) => [
    r, data.captain?.[r] || 0, pct(data.captain?.[r] || 0, total),
    data.vice_captain?.[r] || 0, pct(data.vice_captain?.[r] || 0, total),
  ]);

  // Inning breakdown
  const inningRows: (string | number)[][] = [];
  for (const [key, label] of [['bat_first_inning', 'Batting 1st'], ['bowl_first_inning', 'Batting 2nd']] as [string, string][]) {
    const cData = data.captain_by_inning?.[key] || {};
    const vcData = data.vice_captain_by_inning?.[key] || {};
    for (const r of roles) {
      if ((cData[r] || 0) + (vcData[r] || 0) > 0) {
        inningRows.push([`${label} - ${r}`, cData[r] || 0, pct(cData[r] || 0, total), vcData[r] || 0, pct(vcData[r] || 0, total)]);
      }
    }
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.7 }}>{total} matches analyzed</Typography>
      <StyledTable headers={['Role', 'Captain', 'C %', 'Vice-Captain', 'VC %']} rows={mainRows} />
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, opacity: 0.7 }}>By Inning Dimension</Typography>
      <StyledTable headers={['Dimension', 'Captain', 'C %', 'Vice-Captain', 'VC %']} rows={inningRows} />
    </Box>
  );
};

const CvcPointDiffRenderer: React.FC<{ data: any }> = ({ data }) => {
  if (!data) return null;
  const total = data.total_matches || 1;
  const ranges = ['>100', '50-100', '20-50', '0-20'];
  const rows = ranges.map((r) => [
    r, data.c_vc_diff?.[r] || 0, pct(data.c_vc_diff?.[r] || 0, total),
    data.vc_third_diff?.[r] || 0, pct(data.vc_third_diff?.[r] || 0, total),
  ]);
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.7 }}>{total} matches</Typography>
      <StyledTable headers={['Points Gap', 'C–VC Count', 'C–VC %', 'VC–3rd Count', 'VC–3rd %']} rows={rows} />
    </Box>
  );
};

const TeamCombinationRenderer: React.FC<{ data: any }> = ({ data }) => {
  if (!data) return null;
  const total = data.total_matches || 1;
  const combos = data.combinations || {};
  const rows = Object.entries(combos)
    .filter(([, c]) => (c as number) > 0)
    .map(([k, c]) => [k, c as number, pct(c as number, total)]);
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.7 }}>Bat-first vs Bat-second — {total} matches</Typography>
      <StyledTable headers={['Combination', 'Count', '%']} rows={rows} />
    </Box>
  );
};

const InningContributionRenderer: React.FC<{ data: any }> = ({ data }) => {
  const theme = useTheme();
  if (!data) return null;
  const pieData = [
    { name: '1st Inning Only', value: data.first_inning_only || 0 },
    { name: '2nd Inning Only', value: data.second_inning_only || 0 },
    { name: 'Both Innings', value: data.both_innings || 0 },
  ].filter((d) => d.value > 0);

  const byRole = data.by_role || {};
  const roleRows = Object.entries(byRole).map(([role, counts]: [string, any]) => {
    const t = (counts.first || 0) + (counts.second || 0) + (counts.both || 0);
    return [role, counts.first || 0, counts.second || 0, counts.both || 0, t];
  });

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.7 }}>
        {data.total_players_analyzed} player-slots (WK merged into BAT, &gt;20 pts threshold)
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={3}
                label={({ name, percent }) => `${name}: ${(Number(percent) * 100).toFixed(0)}%`} labelLine={false}>
                {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
        </Grid>
        <Grid item xs={12} md={7}>
          <StyledTable headers={['Role', '1st Inn', '2nd Inn', 'Both', 'Total']} rows={roleRows} />
        </Grid>
      </Grid>
    </Box>
  );
};

const CvcBatPositionRenderer: React.FC<{ data: any }> = ({ data }) => {
  if (!data) return null;
  const positions = ['Opener (1-2)', '#3', '#4-5', 'Lower Order (>5)'];
  const cTotal = data.captain_batting_total || 1;
  const vcTotal = data.vc_batting_total || 1;
  const rows = positions.map((p) => [
    p, data.captain_positions?.[p] || 0, pct(data.captain_positions?.[p] || 0, cTotal),
    data.vc_positions?.[p] || 0, pct(data.vc_positions?.[p] || 0, vcTotal),
  ]);
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.7 }}>
        Only when batting is primary contributor (bat_pts &gt; bowl_pts). C: {data.captain_batting_total}, VC: {data.vc_batting_total}
      </Typography>
      <StyledTable headers={['Bat Position', 'Captain', 'C %', 'Vice-Captain', 'VC %']} rows={rows} />
    </Box>
  );
};

const CaptainSelectionPercRenderer: React.FC<{ data: any }> = ({ data }) => {
  if (!data) return null;
  const total = data.total_matches || 1;
  const buckets = ['>50', '30-50', '20-30', '10-20', '3-10', '1-3', '<1'];
  const rows = buckets.map((b) => [
    `${b}%`, data.captain_by_c_perc?.[b] || 0, pct(data.captain_by_c_perc?.[b] || 0, total),
    data.vc_by_c_perc?.[b] || 0, pct(data.vc_by_c_perc?.[b] || 0, total),
  ]);
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.7 }}>
        Public c% of actual dream team captain/VC — {total} matches
      </Typography>
      <StyledTable headers={['C% Bucket', 'Captain Count', 'Captain %', 'VC Count', 'VC %']} rows={rows} />
    </Box>
  );
};

const SecondInningAnalysisRenderer: React.FC<{ data: any }> = ({ data }) => {
  if (!data) return null;
  const total = data.total_matches || 1;
  const combos = data.combinations || {};
  const comboRows = Object.entries(combos).map(([k, c]) => [k, c as number, pct(c as number, total)]);

  const cRoles = data.captain_roles || {};
  const vcRoles = data.vc_roles || {};
  const allRoles = [...new Set([...Object.keys(cRoles), ...Object.keys(vcRoles)])];
  const roleRows = allRoles.map((r) => [r, cRoles[r] || 0, pct(cRoles[r] || 0, total), vcRoles[r] || 0, pct(vcRoles[r] || 0, total)]);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.7 }}>
        Top 5 2nd-inning contributors from dream team — {total} matches
      </Typography>
      <Typography variant="caption" sx={{ mb: 1, display: 'block', opacity: 0.5 }}>
        Bat-first → bowl+field pts | Bat-second → batting pts. Format: bat_second_count - bat_first_count
      </Typography>
      <StyledTable headers={['Combo (2ndBat - 1stBat)', 'Count', '%']} rows={comboRows} />
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, opacity: 0.7 }}>C/VC Roles (2nd inning mini-DT)</Typography>
      <StyledTable headers={['Role', 'Captain', 'C %', 'Vice-Captain', 'VC %']} rows={roleRows} />
    </Box>
  );
};

const CvcPairAnalysisRenderer: React.FC<{ data: any }> = ({ data }) => {
  if (!data) return null;
  const total = data.total_matches || 1;
  const pairs = data.pairs || {};

  const cRolesSet = new Set<string>();
  const vcRolesSet = new Set<string>();

  Object.keys(pairs).forEach(pair => {
    const [c, vc] = pair.split(':');
    if (c) cRolesSet.add(c);
    if (vc) vcRolesSet.add(vc);
  });

  // Custom sort function to keep BAT, BOWL, ALL, WK in order
  const roleOrder: Record<string, number> = { 'BAT': 1, 'BOWL': 2, 'ALL': 3, 'WK': 4 };
  const sortRoles = (a: string, b: string) => {
    const aRole = a.replace(/[0-9]/g, '');
    const bRole = b.replace(/[0-9]/g, '');
    const aNum = a.replace(/[^0-9]/g, '');
    const bNum = b.replace(/[^0-9]/g, '');
    if (roleOrder[aRole] !== roleOrder[bRole]) return (roleOrder[aRole] || 9) - (roleOrder[bRole] || 9);
    return aNum.localeCompare(bNum);
  };

  const cRoles = Array.from(cRolesSet).sort(sortRoles);
  const vcRoles = Array.from(vcRolesSet).sort(sortRoles);

  const headers = ['Captain \\ VC', ...vcRoles];

  const rows = cRoles.map(cRole => {
    const row = vcRoles.map(vcRole => {
      const count = pairs[`${cRole}:${vcRole}`] || 0;
      if (count === 0) return '-';
      return `${count} (${pct(count, total)})`;
    });
    return [cRole, ...row];
  });

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.7 }}>
        C/VC Role and Inning pairings (e.g. BAT1 = Batting 1st Inning) — {total} matches
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <StyledTable headers={headers} rows={rows} />
      </Box>
    </Box>
  );
};

const DtSelectionPercDistRenderer: React.FC<{ data: any }> = ({ data }) => {
  if (!data) return null;
  const total = data.total_matches || 1;
  const dist = data.distribution || {};

  const ranges = ['<10%', '10-30%', '30-60%', '>60%'];
  const countBuckets = ['0', '1-2', '3-4', '5-6', '7-8', '9-10', '11'];

  const headers = ['Selection %', ...countBuckets];

  const rows = ranges.map(range => {
    const bucketData = dist[range] || {};
    const row = countBuckets.map(cb => {
      const count = bucketData[cb] || 0;
      if (count === 0) return '-';
      return `${count} (${pct(count, total)})`;
    });
    return [range, ...row];
  });

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.7 }}>
        Matches grouped by count of DT players in each selection bucket — {total} matches
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <StyledTable headers={headers} rows={rows} />
      </Box>
    </Box>
  );
};

const HighSelFailsRenderer: React.FC<{ data: any }> = ({ data }) => {
  if (!data) return null;
  const totalFails = data.total_fails || 0;
  const totalMatches = data.total_matches || 1;
  const failsByRole = data.fails_by_role || {};

  const roles = Object.keys(failsByRole);
  const rows = roles.map(r => [
    r, failsByRole[r] || 0, pct(failsByRole[r] || 0, totalFails)
  ]);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.7 }}>
        Players with &gt;60% selection who played but failed to make DT — {totalMatches} matches
      </Typography>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
        Total Fails: {totalFails}
      </Typography>
      <StyledTable headers={['Role', 'Fail Count', '% of Fails']} rows={rows} />
    </Box>
  );
};

const FirstInningAnalysisRenderer: React.FC<{ data: any }> = ({ data }) => {
  if (!data) return null;
  const total = data.total_matches || 1;
  const combos = data.combinations || {};
  const comboRows = Object.entries(combos).map(([k, c]) => [k, c as number, pct(c as number, total)]);

  const cRoles = data.captain_roles || {};
  const vcRoles = data.vc_roles || {};
  const allRoles = [...new Set([...Object.keys(cRoles), ...Object.keys(vcRoles)])];
  const roleRows = allRoles.map((r) => [r, cRoles[r] || 0, pct(cRoles[r] || 0, total), vcRoles[r] || 0, pct(vcRoles[r] || 0, total)]);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.7 }}>
        Top 5 1st-inning contributors from dream team — {total} matches
      </Typography>
      <Typography variant="caption" sx={{ mb: 1, display: 'block', opacity: 0.5 }}>
        Bat-first → batting pts | Bat-second → bowl+field pts. Format: bat_first_count - bat_second_count
      </Typography>
      <StyledTable headers={['Combo (1stBat - 2ndBat)', 'Count', '%']} rows={comboRows} />
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, opacity: 0.7 }}>C/VC Roles (1st inning mini-DT)</Typography>
      <StyledTable headers={['Role', 'Captain', 'C %', 'Vice-Captain', 'VC %']} rows={roleRows} />
    </Box>
  );
};

const InningCorrelationAnalysisRenderer: React.FC<{ data: any }> = ({ data }) => {
  if (!data) return null;
  const total = data.total_matches || 1;
  const correlations = data.correlations || {};

  const secondCombosSet = new Set<string>();
  Object.values(correlations).forEach((secondMap: any) => {
    Object.keys(secondMap).forEach(k => secondCombosSet.add(k));
  });

  const secondCombos = Array.from(secondCombosSet).sort((a, b) => parseInt(b.split('-')[0]) - parseInt(a.split('-')[0]));
  const firstCombos = Object.keys(correlations).sort((a, b) => parseInt(b.split('-')[0]) - parseInt(a.split('-')[0]));

  const headers = ['1st Inn \\ 2nd Inn', ...secondCombos];
  const rows = firstCombos.map(firstCombo => {
    const row = secondCombos.map(secondCombo => {
      const count = correlations[firstCombo][secondCombo] || 0;
      if (count === 0) return '-';
      return `${count} (${pct(count, total)})`;
    });
    return [firstCombo, ...row];
  });

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.7 }}>
        Correlation between 1st Inning and 2nd Inning Compositions — {total} matches
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <StyledTable headers={headers} rows={rows} />
      </Box>
    </Box>
  );
};

// ── Stat metadata ──

const STAT_CONFIG: Record<string, { icon: React.ReactElement; renderer: React.FC<{ data: any }> }> = {
  CVC_DISTRIBUTION: { icon: <AnalyticsIcon />, renderer: CvcDistributionRenderer },
  CVC_POINT_DIFF: { icon: <TrendingUpIcon />, renderer: CvcPointDiffRenderer },
  TEAM_COMBINATION: { icon: <GroupWorkIcon />, renderer: TeamCombinationRenderer },
  INNING_CONTRIBUTION: { icon: <PieChartIcon />, renderer: InningContributionRenderer },
  CVC_BAT_POSITION: { icon: <PersonPinIcon />, renderer: CvcBatPositionRenderer },
  CAPTAIN_SELECTION_PERC: { icon: <PercentIcon />, renderer: CaptainSelectionPercRenderer },
  FIRST_INNING_ANALYSIS: { icon: <Filter1Icon />, renderer: FirstInningAnalysisRenderer },
  SECOND_INNING_ANALYSIS: { icon: <Filter2Icon />, renderer: SecondInningAnalysisRenderer },
  INNING_CORRELATION_ANALYSIS: { icon: <CompareArrowsIcon />, renderer: InningCorrelationAnalysisRenderer },
  CVC_PAIR_ANALYSIS: { icon: <CompareArrowsIcon />, renderer: CvcPairAnalysisRenderer },
  DT_SELECTION_PERC_DIST: { icon: <PercentIcon />, renderer: DtSelectionPercDistRenderer },
  HIGH_SEL_FAILS: { icon: <AnalyticsIcon />, renderer: HighSelFailsRenderer },
};

// ── Main Page ──

const DreamTeamAnalysis: React.FC = () => {
  const theme = useTheme();
  const [scope, setScope] = useState<'SERIES' | 'FORMAT'>('SERIES');
  const [selectedSeries, setSelectedSeries] = useState<any>(null);
  const [seriesOptions, setSeriesOptions] = useState<any[]>([]);
  const [seriesSearch, setSeriesSearch] = useState('');
  const [matchFormat, setMatchFormat] = useState('T20');
  const [gender, setGender] = useState('');
  const [selectedGround, setSelectedGround] = useState<any>(null);
  const [groundOptions, setGroundOptions] = useState<any[]>([]);
  const [groundSearch, setGroundSearch] = useState('');
  const [cutoff, setCutoff] = useState('2020-01-01');
  const [loading, setLoading] = useState(false);

  const [editingAnalysis, setEditingAnalysis] = useState<any>(null);

  const handleUpdateMeta = async () => {
    if (!editingAnalysis) return;
    try {
      await dreamTeamAnalysisApi.update(editingAnalysis.id, {
        custom_label: editingAnalysis.custom_label,
        is_popular: editingAnalysis.is_popular,
        is_visible: editingAnalysis.is_visible,
        order: editingAnalysis.order,
      });
      setEditingAnalysis(null);
      fetchExistingList(listPage);
    } catch (err) {
      console.error(err);
    }
  };

  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Existing analyses listing
  const [existingList, setExistingList] = useState<any[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [listTotal, setListTotal] = useState(0);
  const PAGE_SIZE = 15;

  const fetchExistingList = useCallback(async (page = 1) => {
    setListLoading(true);
    try {
      const res = await dreamTeamAnalysisApi.getAll({ page, page_size: PAGE_SIZE });
      setExistingList(res.data.results || []);
      setListTotal(res.data.count || 0);
      setListPage(page);
    } catch { /* ignore */ }
    finally { setListLoading(false); }
  }, []);

  // Load list on mount
  useEffect(() => { fetchExistingList(1); }, [fetchExistingList]);

  const handleViewExisting = useCallback(async (id: number) => {
    setLoading(true); setError(null);
    try {
      const res = await dreamTeamAnalysisApi.getById(id);
      setResult(res.data);
    } catch (err: any) { setError(err?.response?.data?.error || 'Failed to load analysis.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (seriesSearch.length < 2) return;
    const t = setTimeout(async () => {
      try { const r = await seriesApi.getAll({ search: seriesSearch, page_size: 20 }); setSeriesOptions(r.data.results || []); } catch { /* */ }
    }, 300);
    return () => clearTimeout(t);
  }, [seriesSearch]);

  useEffect(() => {
    if (groundSearch.length < 2) return;
    const t = setTimeout(async () => {
      try { const r = await groundApi.autocomplete({ search: groundSearch, page_size: 20 }); setGroundOptions(r.data.results || []); } catch { /* */ }
    }, 300);
    return () => clearTimeout(t);
  }, [groundSearch]);

  const handleRun = useCallback(async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      const payload: any = { scope, cutoff };
      if (scope === 'SERIES') {
        if (!selectedSeries) { setError('Please select a series.'); setLoading(false); return; }
        payload.series_id = selectedSeries.id;
      } else {
        payload.match_format = matchFormat;
        if (gender) payload.gender = gender;
        if (selectedGround) payload.ground_id = selectedGround.id;
      }
      await dreamTeamAnalysisApi.run(payload);
      if (scope === 'SERIES' && selectedSeries) {
        const res = await dreamTeamAnalysisApi.getBySeries(selectedSeries.id);
        setResult(res.data);
      } else {
        const res = await dreamTeamAnalysisApi.getAll({ scope: 'FORMAT', match_format: matchFormat, ...(gender ? { gender } : {}), page_size: 1 } as any);
        if (res.data.results?.length) { const d = await dreamTeamAnalysisApi.getById(res.data.results[0].id); setResult(d.data); }
      }
      // Refresh the list
      fetchExistingList(1);
    } catch (err: any) { setError(err?.response?.data?.error || err?.message || 'Analysis failed.'); }
    finally { setLoading(false); }
  }, [scope, selectedSeries, matchFormat, gender, selectedGround, cutoff, fetchExistingList]);

  const totalPages = Math.ceil(listTotal / PAGE_SIZE);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={800} sx={{ background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`, backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 0.5 }}>
          Dream Team Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary">Analyze historical dream team patterns — C/VC distribution, point gaps, batting positions, team combos, inning contributions & more.</Typography>
      </Box>

      {/* Filter Bar */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, background: alpha(theme.palette.background.paper, 0.8), backdropFilter: 'blur(10px)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small"><InputLabel>Scope</InputLabel>
              <Select value={scope} label="Scope" onChange={(e) => setScope(e.target.value as any)}><MenuItem value="SERIES">By Series</MenuItem><MenuItem value="FORMAT">By Format</MenuItem></Select>
            </FormControl>
          </Grid>
          {scope === 'SERIES' && (
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete size="small" options={seriesOptions} getOptionLabel={(o: any) => o.start_date ? `${o.name} (${o.start_date.split('T')[0]})` : o.name || ''} value={selectedSeries}
                onChange={(_e, v) => setSelectedSeries(v)} onInputChange={(_e, v) => setSeriesSearch(v)}
                renderInput={(p) => <TextField {...p} label="Series" placeholder="Search..." />}
                isOptionEqualToValue={(o, v) => o.id === v?.id} noOptionsText={seriesSearch.length < 2 ? 'Type to search...' : 'No results'} />
            </Grid>
          )}
          {scope === 'FORMAT' && (<>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small"><InputLabel>Format</InputLabel>
                <Select value={matchFormat} label="Format" onChange={(e) => setMatchFormat(e.target.value)}>
                  <MenuItem value="T20">T20</MenuItem><MenuItem value="ODI">ODI</MenuItem><MenuItem value="TEST">Test</MenuItem><MenuItem value="T10">T10</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small"><InputLabel>Gender</InputLabel>
                <Select value={gender} label="Gender" onChange={(e) => setGender(e.target.value)}>
                  <MenuItem value="">All</MenuItem><MenuItem value="M">Male</MenuItem><MenuItem value="F">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Autocomplete size="small" options={groundOptions} getOptionLabel={(o: any) => `${o.name}, ${o.city}` || ''} value={selectedGround}
                onChange={(_e, v) => setSelectedGround(v)} onInputChange={(_e, v) => setGroundSearch(v)}
                renderInput={(p) => <TextField {...p} label="Ground (opt)" placeholder="Search..." />}
                isOptionEqualToValue={(o, v) => o.id === v?.id} noOptionsText={groundSearch.length < 2 ? 'Type...' : 'None'} />
            </Grid>
          </>)}
          <Grid item xs={6} sm={4} md={2}>
            <TextField size="small" label="Cutoff" type="date" fullWidth value={cutoff} onChange={(e) => setCutoff(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Button fullWidth variant="contained" onClick={handleRun} disabled={loading}
              startIcon={loading ? <CircularProgress size={18} /> : <PlayArrowIcon />}
              sx={{ height: 40, borderRadius: 2, textTransform: 'none', fontWeight: 700, background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})` }}>
              {loading ? 'Running...' : 'Run Analysis'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {/* Detail view */}
      {result && (
        <Box>
          <Button size="small" onClick={() => setResult(null)} sx={{ mb: 2, textTransform: 'none' }}>← Back to list</Button>
          <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
            <Chip label={`${result.matches_analyzed} matches`} color="primary" variant="outlined" size="small" />
            <Chip label={result.scope_display} variant="outlined" size="small" />
            {result.series_name && <Chip label={result.series_name} variant="outlined" size="small" />}
            {result.match_format && <Chip label={result.match_format} variant="outlined" size="small" />}
            {result.gender && <Chip label={result.gender === 'M' ? 'Male' : 'Female'} variant="outlined" size="small" />}
            {result.ground_name && <Chip label={result.ground_name} variant="outlined" size="small" />}
            <Chip label={`Cutoff: ${result.cutoff_date}`} variant="outlined" size="small" color="secondary" />
          </Stack>

          <Grid container spacing={3}>
            {result.stats?.map((stat: any) => {
              const cfg = STAT_CONFIG[stat.stat_type];
              console.log(stat.stat_type, cfg);              const Renderer = cfg?.renderer;
              return (
                <Grid item xs={12} md={6} key={stat.id}>
                  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: `0 4px 24px ${alpha(theme.palette.primary.main, 0.12)}` } }}>
                    <CardHeader
                      avatar={<Box sx={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>{cfg?.icon || <AnalyticsIcon />}</Box>}
                      title={stat.stat_type_display} titleTypographyProps={{ fontWeight: 700, fontSize: 15 }} />
                    <Divider />
                    <CardContent sx={{ pt: 2 }}>
                      {Renderer ? <Renderer data={stat.data} /> : <pre style={{ fontSize: 11, overflow: 'auto', maxHeight: 300 }}>{JSON.stringify(stat.data, null, 2)}</pre>}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Existing analyses listing (shown when no detail is selected) */}
      {!result && !loading && (
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
            <Typography variant="subtitle1" fontWeight={700}>Saved Analyses ({listTotal})</Typography>
            {listLoading && <CircularProgress size={18} />}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Scope</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Label / Series</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Format</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Gender</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Matches</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Stats</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {existingList.length === 0 && !listLoading && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.disabled' }}>No analyses found. Use the form above to run one.</TableCell></TableRow>
                )}
                {existingList.map((item: any) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
                    onClick={() => handleViewExisting(item.id)}
                  >
                    <TableCell><Chip label={item.scope_display} size="small" variant="outlined" sx={{ fontSize: 11 }} /></TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 500, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.custom_label || item.series_name || item.ground_name || '-'}
                      {item.slug && <Typography variant="caption" display="block" color="text.secondary">{item.slug}</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{item.match_format || '-'}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{item.gender === 'M' ? 'Male' : item.gender === 'F' ? 'Female' : '-'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600 }}>{item.matches_analyzed}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>{item.stat_count}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      <Stack direction="row" spacing={0.5}>
                        {item.is_popular && <Chip label="Popular" size="small" color="primary" sx={{ height: 20, fontSize: 10 }} />}
                        {!item.is_visible && <Chip label="Hidden" size="small" color="default" sx={{ height: 20, fontSize: 10 }} />}
                        <Chip label={`Ord: ${item.order}`} size="small" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingAnalysis({ ...item }); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, py: 1.5 }}>
              <Button size="small" disabled={listPage <= 1} onClick={() => fetchExistingList(listPage - 1)} sx={{ textTransform: 'none', minWidth: 32 }}>Prev</Button>
              <Typography variant="body2" sx={{ lineHeight: '30px', color: 'text.secondary' }}>{listPage} / {totalPages}</Typography>
              <Button size="small" disabled={listPage >= totalPages} onClick={() => fetchExistingList(listPage + 1)} sx={{ textTransform: 'none', minWidth: 32 }}>Next</Button>
            </Box>
          )}
        </Paper>
      )}

      <Dialog open={!!editingAnalysis} onClose={() => setEditingAnalysis(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Analysis Metadata</DialogTitle>
        <DialogContent dividers>
          {editingAnalysis && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField fullWidth label="Custom Label" value={editingAnalysis.custom_label || ''} onChange={(e) => setEditingAnalysis({ ...editingAnalysis, custom_label: e.target.value })} size="small" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth type="number" label="Order" value={editingAnalysis.order || 0} onChange={(e) => setEditingAnalysis({ ...editingAnalysis, order: parseInt(e.target.value) || 0 })} size="small" />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel control={<Switch checked={editingAnalysis.is_popular} onChange={(e) => setEditingAnalysis({ ...editingAnalysis, is_popular: e.target.checked })} />} label="Is Popular" />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel control={<Switch checked={editingAnalysis.is_visible} onChange={(e) => setEditingAnalysis({ ...editingAnalysis, is_visible: e.target.checked })} />} label="Is Visible" />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingAnalysis(null)}>Cancel</Button>
          <Button onClick={handleUpdateMeta} variant="contained" disableElevation>Save Changes</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DreamTeamAnalysis;

